<template>
    <div style="text-align:left;position:relative;margin-top:-2px;">
        <div class="instructions kgr-postfix kgr-postfix--between" style="overflow: hidden">
            <div class="kgr-postfix__fixed">
                <kgr-button
                    v-show="!signing && !value.declined && !value.signed && !value.mandatory"
                    class="kgr-button--default kgr-backgroundColor--gray kgr-button"
                    small
                    :title="$t('Klicken Sie hier, wenn Sie diese Unterschrift nicht leisten können oder wollen')"
                    :value="$t('ablehnen')"
                    style="height:20px;background-color: #888;border:none"
                    icon="close"
                    @click="decline"
                />
            </div>
            <div class="kgr-postfix__fixed">
                <kgr-button
                    v-if="showOptionsAndSaveButton"
                    icon="check"
                    class="slideInUp"
                    small
                    value="Bestätigen"
                    style="height:20px"
                    @click="saveSignature"
                />
            </div>
        </div>
        <div
            v-if="showOptionsAndSaveButton"
            class="slideIn settings"
        >
            <div style="float:left">
                <div
                    v-for="(font, i) in fontFamilies"
                    :key="font"
                    class="font-box"
                    :class="{ 'box-selected': fontFamily === font }"
                    :style="`font-family: '${font}'`"
                    @click="chooseFont(font)"
                >
                    {{ $t('Schrift') }}{{ i + 1 }}
                </div>
            </div>
            <div style="float:right;">
                <div
                    v-for="color in fontColors"
                    :key="color"
                    class="color-box"
                    :class="{ 'box-selected': fontColor === color }"
                >
                    <div
                        :style="`background-color: ${color}`"
                        @click="chooseColor(color)"
                    ></div>
                </div>
            </div>
        </div>
        <div class="kgr-postfix kgr-postfix--center">
            <div class="kgr-postfix__fluid">
                <input
                    ref="input"
                    v-model.trim="name"
                    type="text"
                    :maxlength="30"
                    :disabled="value.signed || value.declined"
                    class="kgr-input kgr-input--inline kgr-input--small signature-input no-arrows"
                    :style="{ 'font-family': fontFamily, 'color': fontColor, 'background-color': backgroundColor, 'font-size': `${fontSizePx}px` }"
                    :class="{ shake: showError }"
                    :placeholder="placeholder"
                    @animationend="showError = false"
                    @keyup="saveIfEnter"
                    @focus="focusInput"
                />
            </div>
            <div class="kgr-postfix__fixed" style="padding-right: 6px;">
                <kgr-loading
                    v-if="signing"
                    small
                />
                <kgr-icon-close
                    v-else-if="!value.declined && !value.signed && !value.mandatory"
                    :title="$t('Klicken Sie hier, wenn Sie diese Unterschrift nicht leisten können oder wollen')"
                    @click="decline"
                />
            </div>
        </div>

        <div style="position:absolute; top:10px; right:26px; transform: translateX(100%);height:100%;z-index: 20">
            <kgr-icon
                v-if="value.signed || value.declined"
                icon-class="undo"
                style="display: inline-block;"
                small
                @click="reOpen"
            />
        </div>
    </div>
</template>

<script>
import Icon from '../../common/icon/Icon.vue';
import IconClose from '../../common/icon/IconClose.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import LetterSignature from '../../../js/dto/letter/signature/LetterSignature.js';
import Loading from '../../common/Loading.vue';

export default {
    components: {
        KgrButton,
        'kgr-loading': Loading,
        'kgr-icon-close': IconClose,
        'kgr-icon': Icon,
    },
    props: {
        value: {
            type: LetterSignature,
            required: false,
            default: false,
        },
        iconAdd: {
            type: Boolean,
            required: false,
            default: false,
        },
        pending: {
            type: Boolean,
            required: false,
            example: false,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        fontColors: {
            type: Array,
            required: false,
            default: () => ['#0e1ad3', 'black', 'green'],
        },
        fontFamilies: {
            type: Array,
            required: false,
            default: () => ['Nothing You Could Do', 'Kalam', 'Darker Grotesque'],
        },
        settings: {
            type: Object,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            name: this.value.name,
            signing: false,
            showError: false,
            focusActive: false,
            fontFamily: null,
            fontColor: null,
            fontSizePx: 25,
        };
    },
    computed: {
        showOptionsAndSaveButton() {
            return this.focusActive && this.name && this.name.length > 0 && !this.value.signed;
        },
        placeholder() {
            if (this.value.declined) {
                return this.$t('Unterschrift unterlassen');
            }
            return this.value.mandatory ? this.$t('Geben Sie hier Ihren Namen ein') : this.$t('Geben Sie Ihren Namen ein (optional)');
        },
        backgroundColor() {
            if (this.value.signed) {
                return '#c5f9d5';
            }
            if (this.value.declined) {
                return '#efefef';
            }
            return '#fbf9b5';
        },
    },
    watch: {
        settings: {
            immediate: true,
            handler(newValue) {
                if (this.name === '' || this.name === null) {
                    if (newValue.fontFamily) {
                        this.fontFamily = newValue.fontFamily;
                    }
                    if (newValue.fontColor) {
                        this.fontColor = newValue.fontColor;
                    }
                }
            },
        },
    },
    mounted() {
        this.fontFamily = this.settings.fontFamily ?? this.fontFamilies[0] ?? 'Kalam';
        this.fontColor = this.settings.fontColor ?? this.fontColors[0] ?? 'blue';
    },
    methods: {
        focus() {
            this.$refs.input.focus();
            this.focusActive = true;
        },
        chooseFont(font) {
            this.fontFamily = font;
            this.$refs.input.focus();
            this.$emit('settings', { fontColor: this.fontColor, fontFamily: font });
        },
        chooseColor(color) {
            this.fontColor = color;
            this.$refs.input.focus();
            this.$emit('settings', { fontColor: color, fontFamily: this.fontFamily });
        },
        validate() {
            if (!this.name) {
                this.showError = true;
                this.$nextTick(() => {
                    const input = this.$el.querySelector('input');
                    if (input) {
                        input.focus();
                    }
                });

                return false;
            }
            return true;
        },
        reOpen() {
            this.name = '';
            const sig = this.value.clone();
            sig.name = this.name;
            sig.signed = false;
            sig.declined = false;
            this.$emit('reset', sig);
        },
        focusInput() {
            this.focusActive = true;
            this.$emit('inputFocus', this.value);
        },
        saveIfEnter(event) {
            this.focusActive = true;
            if (event.key === 'Enter') {
                this.saveSignature();
            }
        },
        decline() {
            const sig = this.value.clone();
            this.name = '';
            sig.decline();
            this.$emit('decline', sig);
        },
        saveSignature() {
            if (this.value.signed) {
                return;
            }

            if (this.signing || this.pending || this.disabled) {
                return;
            }

            this.focusActive = false;
            if (!this.validate()) {
                return;
            }
            this.signing = true;
            const sig = this.value.clone();
            sig.sign(this.name, this.fontFamily, this.fontColor, this.fontSizePx);

            window.setTimeout(() => {
                this.$emit('sign', sig);
                this.signing = false;
            }, 400);
        },
    },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
@font-face {
    font-family: 'Nothing You Could Do';
    font-weight: var(--kgr-font-weight-normal);
    font-style: normal;
    font-display: fallback;
    src: url('/fonts/special/Nothing_You_Could_Do/NothingYouCouldDo-Regular.ttf');
}

input::placeholder {
    font-family: 'Arial', sans-serif;
    color: #333;
    font-style: italic;
    font-size: 11pt;
}

@keyframes shake {
    0% {
        transform: translateX(0);
    }
    20% {
        transform: translateX(-5px);
    }
    40% {
        transform: translateX(5px);
    }
    60% {
        transform: translateX(-5px);
    }
    80% {
        transform: translateX(5px);
    }
    100% {
        transform: translateX(0);
    }
}

@keyframes slide-in {
    0% {
        opacity: 0;
        transform: translateY(-20px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slide-up {
    0% {
        opacity: 0;
        transform: translateY(20px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

.shake {
    animation: shake 0.3s;
}

.fadeIn {
    animation: fade-in 2s;
}
.slideIn {
    animation: slide-in 1s;
}
.slideInUp {
    animation: slide-up 1s;
}

.enterKey {
    display: inline-block;
    background-color: white;
    border-radius: 4px;
    box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
    padding: 2px 5px 2px 2px;
    height: 24px;
}
.instructions {
    box-sizing: border-box;
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 1;
    width: 100%;
    border: none;
    white-space: normal;
    color: #666666;
    padding: 2px 5px 5px 5px;
}

.settings {
    width: 100%;
    height: 30px;
    position: absolute;
    z-index: 5;
    white-space: normal;
    color: #222222;
    overflow: hidden;
}
.signature-input {
    height: 100%;
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
    font-size: 22px;
    border: none;
    border-radius: revert;
    padding: 10px;
}
.font-box {
    display: inline-block;
    padding: 6px 10px 5px 10px;
}
.font-box:hover {
    background-color: #efefef;
}
.box-selected {
    background-color: #ddd;
}
.color-box {
    display: inline-block;
    width: 28px;
    height: 28px;
    padding: 4px;
}
.color-box div {
    width: 100%;
    height: 100%;
    border-bottom: none;
    cursor: pointer;
    border-radius: 3px;
    z-index: 14;
}
.color-box:hover {
    background-color: #efefef;
}
</style>
